import type { ReactNode } from 'react';
import { RESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { Message } from '@gonfalon/messages/Message';
import { getAnnotationQuery } from '@gonfalon/rest-api/queries/annotations';
import { Heading, Text } from '@launchpad-ui/components';
import { useQuery } from '@tanstack/react-query';

import { ReviewForm } from './ReviewForm';

import styles from './ReviewTab.module.css';

export interface ReviewTabProps {
  traceId: string;
  spanId: string;
  spanTimestamp: string;
  projectKey: string;
  envKey: string;
  actualOutput?: string;
}

const ReviewTabMessage = ({ children }: { children: ReactNode }) => (
  <div className={styles.message}>
    <Heading level={3}>
      <Message id="observability.spanReview.tabLabel" />
    </Heading>
    <Text>{children}</Text>
  </div>
);

export const ReviewTab = ({ traceId, spanId, spanTimestamp, projectKey, envKey, actualOutput }: ReviewTabProps) => {
  const annotationQuery = useQuery({
    ...getAnnotationQuery({ projKey: projectKey, envKey, traceId, spanId }),
    retry: (failureCount, error) => {
      if (error instanceof RESTAPIError && error.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Only the first load is a hard loading state. A settled 404 has no cached data, so a background
  // refetch (e.g. on window focus) reverts `status` to pending — gating on `isFetched` keeps that
  // from collapsing the mounted form and discarding an in-progress draft.
  if (annotationQuery.isPending && !annotationQuery.isFetched) {
    return (
      <ReviewTabMessage>
        <Message id="observability.spanReview.loading" />
      </ReviewTabMessage>
    );
  }

  const annotationNotFound = annotationQuery.error instanceof RESTAPIError && annotationQuery.error.status === 404;
  if (annotationQuery.isError && !annotationNotFound) {
    return (
      <ReviewTabMessage>
        <Message id="observability.spanReview.loadError" />
      </ReviewTabMessage>
    );
  }

  return (
    <ReviewForm
      key={`${projectKey}:${envKey}:${traceId}:${spanId}`}
      annotation={annotationQuery.data}
      traceId={traceId}
      spanId={spanId}
      spanTimestamp={spanTimestamp}
      projectKey={projectKey}
      envKey={envKey}
      actualOutput={actualOutput}
    />
  );
};
